import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';


export function headingSlug(value) {
  const normalized = String(value || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase()
    .replace(/[^a-z0-9\u00c0-\u024f]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return normalized || 'section';
}

function childText(children) {
  return React.Children.toArray(children)
    .map((child) => (typeof child === 'string' || typeof child === 'number' ? String(child) : ''))
    .join('');
}

export function prepareMarkdown(markdown) {
  const lines = String(markdown || '').replace(/\r\n/g, '\n').split('\n');
  const prepared = [];
  let beforeFirstSection = true;
  let skippingTopQuote = false;

  lines.forEach((line, index) => {
    if (beforeFirstSection && /^##\s+/.test(line)) {
      beforeFirstSection = false;
      skippingTopQuote = false;
    }
    if (beforeFirstSection && index === 0 && /^#\s+/.test(line)) return;
    if (beforeFirstSection && /^(Status|Last (verified|reviewed)):\s*/i.test(line)) return;
    if (beforeFirstSection && /^>\s*\*\*[^*]+:\*\*/.test(line)) {
      skippingTopQuote = true;
      return;
    }
    if (beforeFirstSection && skippingTopQuote) {
      if (/^>/.test(line) || /^\s*$/.test(line)) return;
      skippingTopQuote = false;
    }
    prepared.push(line);
  });

  return prepared.join('\n').replace(/^\s+/, '');
}

export function parseHeadings(markdown) {
  const seen = new Map();
  return String(markdown || '')
    .split('\n')
    .map((line, index) => {
      const match = /^(#{2,4})\s+(.+?)\s*$/.exec(line);
      if (!match) return null;
      const title = match[2].replace(/[*_`]/g, '').trim();
      const base = headingSlug(title);
      const count = (seen.get(base) || 0) + 1;
      seen.set(base, count);
      return {
        depth: match[1].length,
        title,
        id: count === 1 ? base : `${base}-${count}`,
        line: index + 1,
      };
    })
    .filter(Boolean);
}

function safeUri(uri) {
  if (!uri) return '';
  if (uri.startsWith('#') || uri.startsWith('manual:')) return uri;
  if (uri.startsWith('https://') || uri.startsWith('mailto:')) return uri;
  return '';
}

export default function MarkdownArticle({ markdown, onInternalNavigate, labels = {} }) {
  const externalLabel = labels.external || 'external';
  const imageUnavailable = labels.imageUnavailable || 'Image unavailable';
  const tableScrollable = labels.tableScrollable || 'Scrollable table';
  const prepared = useMemo(() => prepareMarkdown(markdown), [markdown]);
  const headings = useMemo(() => parseHeadings(prepared), [prepared]);
  const headingByLine = useMemo(
    () => new Map(headings.map((heading) => [heading.line, heading.id])),
    [headings],
  );

  const headingComponent = (Tag) => ({ node, children, ...props }) => {
    const id = headingByLine.get(node?.position?.start?.line) || headingSlug(childText(children));
    return <Tag id={id} tabIndex="-1" {...props}>{children}</Tag>;
  };

  const components = {
    h1: headingComponent('h2'),
    h2: headingComponent('h2'),
    h3: headingComponent('h3'),
    h4: headingComponent('h4'),
    a: ({ href = '', children, ...props }) => {
      if (href.startsWith('manual:')) {
        const target = href.slice('manual:'.length);
        const [slug, fragment] = target.split('#', 2);
        const to = `/manual/${slug}${fragment ? `#${fragment}` : ''}`;
        return (
          <Link to={to} onClick={() => onInternalNavigate?.()} {...props}>
            {children}
          </Link>
        );
      }
      if (href.startsWith('#')) return <a href={href} {...props}>{children}</a>;
      if (href.startsWith('https://') || href.startsWith('mailto:')) {
        return (
          <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
            {children}<span className="manual-external-label"> ({externalLabel})</span>
          </a>
        );
      }
      return <span>{children}</span>;
    },
    img: ({ alt = '' }) => <span role="img" aria-label={alt || imageUnavailable}>{imageUnavailable}</span>,
    table: ({ children, ...props }) => (
      <div className="manual-table-scroll" tabIndex="0" aria-label={tableScrollable}>
        <table {...props}>{children}</table>
      </div>
    ),
  };

  return (
    <div className="manual-markdown">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        skipHtml
        transformLinkUri={safeUri}
        components={components}
      >
        {prepared}
      </ReactMarkdown>
    </div>
  );
}

export function ArticleTableOfContents({
  headings,
  label = 'On this page',
  navigationLabel = 'Article contents',
}) {
  if (!headings.length) return null;
  return (
    <nav className="manual-toc manual-no-print" aria-label={navigationLabel}>
      <strong>{label}</strong>
      <ul>
        {headings.map((heading) => (
          <li key={`${heading.line}-${heading.id}`} className={`manual-toc-depth-${heading.depth}`}>
            <a href={`#${heading.id}`}>{heading.title}</a>
          </li>
        ))}
      </ul>
    </nav>
  );
}
